// src/components/Dashboard/IglooMembers.js
import React, { useEffect, useState } from "react";
import ContentEditable from "react-contenteditable";
import { db, auth, API_BASE_URL } from "../../firebase/firebaseConfig";
import {
  collection,
  doc,
  getDoc,
  setDoc,
  // deleteDoc,
  getDocs,
  updateDoc,
  arrayUnion,
} from "firebase/firestore";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Separator } from "../ui/separator";
import { UserPlus, User, Crown } from "lucide-react";
import ErrorModal from "../ui/ErrorModal";

function IglooMembers({ iglooID, familyID, isInviteView = false, onUpdate, members = [], isController = false, loading = false, controllersLoading = false }) {
  const [familyNames, setFamilyNames] = useState({});
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteKnownName, setInviteKnownName] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [editingFamilyID, setEditingFamilyID] = useState(null);
  const [tempName, setTempName] = useState("");
  const [inviteType, setInviteType] = useState("familyName");
  const [connectedFamilies, setConnectedFamilies] = useState([]);
  const [processingMembers, setProcessingMembers] = useState(new Set());
  const [sendingInvite, setSendingInvite] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const MAX_IGLOO_MEMBERS = 5;

  const toPlainText = (html) => {
    const div = document.createElement('div');
    div.innerHTML = html || '';
    return (div.textContent || '').replace(/\u00A0/g, ' ');
  };

  useEffect(() => {
    loadConnectedFamilies();
  }, [iglooID]);

  useEffect(() => {
    (async () => {
      for (const m of members) {
        if (!familyNames[m.familyID]) {
          const name = await getFamilyDisplayName(m.familyID);
          setFamilyNames((prev) => ({ ...prev, [m.familyID]: name }));
        }
      }
    })();
  }, [members]);

  // const removeFamilyFromIgloo = async (familyIDToRemove) => {
  //   try {
  //     await deleteDoc(doc(db, "iglooInfo", iglooID, "pending", familyIDToRemove));
  //     await deleteDoc(doc(db, "iglooInviteMessage", familyIDToRemove, "iglooIDs", iglooID));

  //     const iglooDocRef = doc(db, "iglooInfo", iglooID);
  //     const docSnap = await getDoc(iglooDocRef);
  //     if (docSnap.exists()) {
  //       const data = docSnap.data();
  //       const controllers = (data.controllers || []).filter(id => id !== familyIDToRemove);
  //       const members = (data.members || []).filter(id => id !== familyIDToRemove);
  //       await setDoc(iglooDocRef, { controllers, members }, { merge: true });
  //     }

  //     await deleteDoc(doc(db, "familyInfo", familyIDToRemove, "igloos", iglooID));
  //     await deleteDoc(doc(db, "familyInfo", familyIDToRemove, "activeIgloos", iglooID));
  //   } catch (error) {
  //     console.error("Error removing family from igloo:", error);
  //   }
  // };


  // const loadFamilyName = async (memberFamilyID) => {
  //   if (!familyNames[memberFamilyID]) {
  //     const name = await getFamilyDisplayName(memberFamilyID);
  //     setFamilyNames((prevNames) => ({
  //       ...prevNames,
  //       [memberFamilyID]: name,
  //     }));
  //   }
  // };

  const getFamilyDisplayName = async (targetFamilyID) => {
    let connectedFamilyRef;
    if (familyID === targetFamilyID) {
      connectedFamilyRef = doc(db, "familyInfo", familyID);
    } else {
      connectedFamilyRef = doc(
        db,
        "familyInfo",
        familyID,
        "connectedFamilies",
        targetFamilyID
      );
    }

    try {
      const docSnap = await getDoc(connectedFamilyRef);
      if (docSnap.exists()) {
        return docSnap.data().name;
      } else {
        const familyDocRef = doc(db, "familyInfo", targetFamilyID);
        const familyDocSnap = await getDoc(familyDocRef);
        if (familyDocSnap.exists()) {
          return familyDocSnap.data().name;
        } else {
          return "Unknown Family";
        }
      }
    } catch (error) {
      console.error("Error getting family display name:", error);
      return "Unknown Family";
    }
  };

  const updateFamilyName = async (memberFamilyID, newName) => {
    const connectedFamilyRef = doc(
      db,
      "familyInfo",
      familyID,
      "connectedFamilies",
      memberFamilyID
    );

    const newNameTrimmed = (toPlainText(newName) || "").trim();
    if (!newNameTrimmed) {
      setErrorMessage("The family name cannot be empty.");
      setShowErrorModal(true);
      return;
    }

    if(connectedFamilies.find((family) => (family.name || "").trim() === newNameTrimmed)) {
      setErrorMessage("A different family is already known by this name, please choose another name.");
      setShowErrorModal(true);
      return;
    }
    try {
      await setDoc(connectedFamilyRef, { name: newNameTrimmed }, { merge: true });
      setFamilyNames((prevNames) => ({
        ...prevNames,
        [memberFamilyID]: newNameTrimmed,
      }));
    } catch (error) {
      console.error("Error updating family name:", error);
      setErrorMessage("Error updating family name. Please try again.");
      setShowErrorModal(true);
    }
  };

  const handleNameChange = (event) => {
    setTempName(event.target.value);
  };

  const startEditing = (memberFamilyID) => {
    setEditingFamilyID(memberFamilyID);
    setTempName(familyNames[memberFamilyID]);
  };

  const saveNameChange = (memberFamilyID) => {
    updateFamilyName(memberFamilyID, tempName);
    setEditingFamilyID(null);
  };

  const cancelNameChange = () => {
    setEditingFamilyID(null);
  };

  const loadConnectedFamilies = async () => {
    const connectedFamiliesRef = collection(
      db,
      "familyInfo",
      familyID,
      "connectedFamilies"
    );
    const snapshot = await getDocs(connectedFamiliesRef);
    const families = snapshot.docs.map((doc) => ({
      id: doc.id,
      name: doc.data().name,
    }));
    setConnectedFamilies(families);
  };

  const inviteFamilyToIgloo = async () => {
    if (members.length >= MAX_IGLOO_MEMBERS) {
      setErrorMessage(`This igloo is already at capacity (${MAX_IGLOO_MEMBERS} members maximum). Please remove a member before inviting someone new.`);
      setShowErrorModal(true);
      return;
    }

    setSendingInvite(true);

    let inviteeFamilyID = null;
    try {
      if (inviteType === "email") {
        if (inviteEmail === "") {
          setErrorMessage("Please enter an email.");
          setShowErrorModal(true);
          return;
        }
        if (inviteKnownName.trim() === "") {
          setErrorMessage("Please enter a name for this family.");
          setShowErrorModal(true);
          return;
        }
        inviteeFamilyID = await getFamilyIDByEmail(inviteEmail);
      } else if (inviteType === "familyName") {
        if (!inviteKnownName) {
          setErrorMessage("Please select a family name.");
          setShowErrorModal(true);
          return;
        }
        inviteeFamilyID = connectedFamilies.find((family) => family.name === inviteKnownName)?.id;
      }

      if (!inviteeFamilyID) {
        console.error("Error: inviteeFamilyID is null or undefined.");
        setErrorMessage("The provided information is not associated with any family in the network.");
        setShowErrorModal(true);
        return;
      }

      const pendingRef = doc(db, "iglooInfo", iglooID, "pending", inviteeFamilyID);
      const pendingRefSnapshot = await getDoc(pendingRef);
      if(pendingRefSnapshot.exists()) {
        console.warn("Family invite already exists.");
        setErrorMessage("This family has already been invited to this igloo.");
        setShowErrorModal(true);
        return;
      }
      
      const iglooDocRef = doc(db, "iglooInfo", iglooID);
      const iglooSnapshot = await getDoc(iglooDocRef);
      if(!iglooSnapshot.exists()) {
        const msg = "There was an internal error inviting the family to the igloo. The igloo document does not exits. Please report this.";
        console.error(msg);
        setErrorMessage(msg);
        setShowErrorModal(true);
        return;
      }
      if(iglooSnapshot.data().members.includes(inviteeFamilyID)) {
        console.warn("Family is already in the members list for this igloo.");
        setErrorMessage("The invited family is already a member of this igloo.");
        setShowErrorModal(true);
        return;
      }
      if(iglooSnapshot.data().controllers.includes(inviteeFamilyID)) {
        console.warn("Family is already in the controllers list for this igloo.");
        setErrorMessage("The invited family is already a controller of this igloo.");
        setShowErrorModal(true);
        return;
      }

      if (
        inviteType !== "familyName"
        && connectedFamilies.find((family) => family.name === inviteKnownName.trim())
      ) {
        const msg = "A connected family is already known by this name, please choose another name for the family.";
        console.warn(msg);
        setErrorMessage(msg);
        setShowErrorModal(true);
        return;
      }

      await setDoc(pendingRef, { from: familyID, role: inviteRole });
      if (inviteRole === "controller") {
        await updateDoc(iglooDocRef, { controllers: arrayUnion(inviteeFamilyID) });
      } else {
        await updateDoc(iglooDocRef, { members: arrayUnion(inviteeFamilyID) });
      }
      const inviteMessageRef = doc(db, "iglooInviteMessage", inviteeFamilyID, "iglooIDs", iglooID);
      await setDoc(inviteMessageRef, {});
      
      var inviteeRenamingConflict = false;
      if (inviteType !== "familyName") {
        // Check if the invitee family is already a connected family with a different name
        if (connectedFamilies.find((family) => family.id === inviteeFamilyID && family.name !== inviteKnownName.trim())) {
          inviteeRenamingConflict = true;
        }

        if (!inviteeRenamingConflict) {
          const connectedFamilyRef = doc(db, "familyInfo", familyID, "connectedFamilies", inviteeFamilyID);
          await setDoc(connectedFamilyRef, { name: inviteKnownName.trim() });
        } else {
          // alert the user of the name conflict and ask if they want to continue with the new name or retain the existing name
          const existingFamily = connectedFamilies.find((family) => family.id === inviteeFamilyID);
          const existingName = existingFamily.name;
          const userChoice = window.confirm(
            `This family is already known to you as "${existingName}". ` +
            `Would you like to rename them to "${inviteKnownName.trim()}"?\n\n` +
            `Click "OK" to rename them to "${inviteKnownName.trim()}"\n` +
            `Click "Cancel" to keep the existing name "${existingName}" and continue with the invite`
          );
          
          if (userChoice) {
            const connectedFamilyRef = doc(db, "familyInfo", familyID, "connectedFamilies", inviteeFamilyID);
            await setDoc(connectedFamilyRef, { name: inviteKnownName.trim() }, { merge: true });
            
            // Update local state to reflect the name change
            setFamilyNames((prevNames) => ({
              ...prevNames,
              [inviteeFamilyID]: inviteKnownName.trim(),
            }));
            
            // Update connected families list
            setConnectedFamilies((prevFamilies) =>
              prevFamilies.map((family) =>
                family.id === inviteeFamilyID
                  ? { ...family, name: inviteKnownName.trim() }
                  : family
              )
            );
          }
        }
      }

      console.log("Invite sent successfully!");
      setInviteEmail("");
      setInviteKnownName("");
      onUpdate?.();
    } catch (error) {
      console.error("Error inviting family to igloo:", error);
    } finally {
      setSendingInvite(false);
    }
  };

  const getFamilyIDByEmail = async (email) => {
    // Use cloud function to look up primaryFamilyID by email
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.error("No authenticated user");
        return null;
      }

      const idToken = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/user/getPrimaryFamilyIDByEmail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.found) {
          return data.primaryFamilyID;
        }
      }
      return null;
    } catch (error) {
      console.error("Error getting familyID by email:", error);
      return null;
    }
  };

  const changeMemberRole = async (memberFamilyID, newRole) => {
    const iglooDocRef = doc(db, "iglooInfo", iglooID);

    try {
      const docSnap = await getDoc(iglooDocRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        let controllers = data.controllers || [];
        let members = data.members || [];

        if (newRole === "controller") {
          members = members.filter(id => id !== memberFamilyID);
          if (!controllers.includes(memberFamilyID)) {
            controllers.push(memberFamilyID);
          }
        } else {
          controllers = controllers.filter(id => id !== memberFamilyID);
          if (!members.includes(memberFamilyID)) {
            members.push(memberFamilyID);
          }
        }

        await setDoc(iglooDocRef, { controllers, members }, { merge: true });
      }
    } catch (error) {
      console.error("Error changing member role:", error);
    }
  };

  const canLeaveAsController = () => {
    // Filter out pending controllers
    const activeControllers = members.filter(
      member => member.role === "controller" && !member.isPending
    );
    // Check if there are other active controllers besides the current user
    return activeControllers.length > 1;
  };

  const handleMemberAction = async (memberFamilyID, isCurrentUser) => {
    setProcessingMembers(prev => new Set([...prev, memberFamilyID]));

    try {
      const currentUser = auth.currentUser;
      const idToken = await currentUser.getIdToken();

      const response = await fetch(`${API_BASE_URL}/user/removeMemberFromIgloo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          iglooID,
          memberFamilyID
        }),
      });

      const data = await response.json();
      if (data.success) {
        onUpdate?.();
      } else {
        console.error('Error:', data.error || 'Failed to remove member.');
      }
    } catch (error) {
      console.error('Error handling member action:', error);
    } finally {
      setProcessingMembers(prev => {
        const newSet = new Set(prev);
        newSet.delete(memberFamilyID);
        return newSet;
      });
    }
  };

  // Loading skeleton component
  const MemberSkeleton = () => (
    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg animate-pulse">
      <div className="flex items-center gap-3">
        <div className="h-8 w-8 bg-slate-200 rounded-full"></div>
        <div className="flex-1">
          <div className="h-4 bg-slate-200 rounded w-24 mb-1"></div>
          <div className="h-3 bg-slate-200 rounded w-16"></div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="h-8 w-32 bg-slate-200 rounded"></div>
        <div className="h-8 w-20 bg-slate-200 rounded"></div>
      </div>
    </div>
  );

  return (
    <div className="space-y-3">
      {/* Loading State */}
      {loading && (
        <div className="space-y-3">
          <MemberSkeleton />
          <MemberSkeleton />
          <div className="h-px bg-slate-200"></div>
          {controllersLoading && (
            <div className="h-20 bg-slate-50 rounded-lg animate-pulse flex items-center justify-center">
              <div className="text-slate-400 text-sm">Loading invite options...</div>
            </div>
          )}
        </div>
      )}

      {/* Members List */}
      {!loading && members.map((member) => {
        const isCurrentUser = member.familyID === familyID;
        const isUserController = member.role === "controller";
        
        // Determine if the leave/remove button should be enabled
        const buttonDisabled = isCurrentUser
          ? (isUserController && !canLeaveAsController()) // Disable leave for sole controller
          : (!isController); // Disable remove if not a controller
        
        const isProcessingMember = processingMembers.has(member.familyID);

        return (
          <div key={member.familyID} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-2 sm:p-3 bg-slate-50 rounded-lg gap-2 sm:gap-3">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              <Avatar className="h-6 w-6 sm:h-8 sm:w-8 flex-shrink-0">
                <AvatarFallback className="text-xs">
                  {(familyNames[member.familyID] || "?").substring(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                {isCurrentUser ? (
                  <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                    <span className="font-medium truncate text-sm sm:text-base">
                      {familyNames[member.familyID] || "Loading..."}
                    </span>
                    <Badge variant="outline" className="text-xs flex-shrink-0">
                      You
                    </Badge>
                    {member.isPending && <Badge variant="outline" className="text-xs flex-shrink-0">Pending</Badge>}
                  </div>
                ) : (
                  editingFamilyID === member.familyID ? (
                    <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                      <ContentEditable
                        html={tempName}
                        onChange={handleNameChange}
                        tagName="span"
                        className="text-slate-900 border-b-2 border-blue-600 font-medium outline-none flex-1 text-sm sm:text-base"
                      />
                      {member.isPending && <Badge variant="outline" className="text-xs flex-shrink-0">Pending</Badge>}
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                      <span 
                        onClick={() => !isInviteView && startEditing(member.familyID)}
                        className="font-medium cursor-pointer truncate text-sm sm:text-base"
                      >
                        {familyNames[member.familyID] || "Loading..."}
                      </span>
                      {member.isPending && <Badge variant="outline" className="text-xs flex-shrink-0">Pending</Badge>}
                    </div>
                  )
                )}
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <Select
                value={member.role}
                onValueChange={(value) => changeMemberRole(member.familyID, value)}
                disabled={!isController || member.familyID === familyID}
              >
                <SelectTrigger className="w-full sm:w-32 h-7 sm:h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="controller">Controller</SelectItem>
                </SelectContent>
              </Select>
              
              {!isInviteView && (
                <Button
                  onClick={() => handleMemberAction(member.familyID, isCurrentUser)}
                  disabled={buttonDisabled || isProcessingMember}
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-[85px] h-7 sm:h-8 text-xs sm:text-sm"
                >
                  {isProcessingMember 
                    ? (isCurrentUser ? "Leaving..." : "Removing...") 
                    : (isCurrentUser ? "Leave" : "Remove")}
                </Button>
              )}
            </div>
          </div>
        );
      })}

      {/* Edit Name Actions */}
      {!loading && editingFamilyID && !isInviteView && (
        <div className="flex items-center gap-2 ml-5">
          <Button
            onClick={() => saveNameChange(editingFamilyID)}
            variant="default"
            size="sm"
            className="bg-black text-white hover:bg-black/90 h-7 sm:h-8 text-xs sm:text-sm"
          >
            Save
          </Button>
          <Button
            onClick={cancelNameChange}
            variant="outline"
            size="sm"
            className="h-7 sm:h-8 text-xs sm:text-sm"
          >
            Cancel
          </Button>
        </div>
      )}

      {/* Separator */}
      {!loading && isController && !isInviteView && members.length > 0 && (
        <Separator />
      )}
      
      {/* Invite Section */}
      {!loading && !controllersLoading && isController && !isInviteView && (
        <div className="space-y-3">
          <div className="space-y-3">
            {/* Invite Type Selection */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Select
                value={inviteType}
                onValueChange={setInviteType}
                className="flex-1"
              >
                <SelectTrigger className="h-8 sm:h-10 text-sm">
                  <SelectValue placeholder="Select invite method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="familyName">Invite by Family Name</SelectItem>
                  <SelectItem value="email">Invite by Email</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={inviteRole}
                onValueChange={setInviteRole}
              >
                <SelectTrigger className="w-full sm:w-32 h-8 sm:h-10 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="controller">Controller</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Invite Input Fields */}
            <div className="space-y-3">
              {inviteType === "familyName" && (
                <Select
                  value={inviteKnownName}
                  onValueChange={setInviteKnownName}
                >
                  <SelectTrigger className="w-full h-8 sm:h-10 text-sm">
                    <SelectValue placeholder="Select Family Name" />
                  </SelectTrigger>
                  <SelectContent>
                    {connectedFamilies.sort((a, b) => a.name.localeCompare(b.name)).map((family) => (
                      <SelectItem key={family.id} value={family.name}>
                        {family.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              {inviteType === "email" && (
                <div className="space-y-3">
                  <Input
                    type="email"
                    placeholder="Enter email address"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="h-8 sm:h-10 text-sm"
                  />
                  <Input
                    type="text"
                    placeholder="What name do you know this family by?"
                    value={inviteKnownName}
                    onChange={(e) => setInviteKnownName(e.target.value)}
                    className="h-8 sm:h-10 text-sm"
                  />
                </div>
              )}

              <Button
                onClick={inviteFamilyToIgloo}
                disabled={sendingInvite}
                variant="default"
                className="w-full flex items-center justify-center gap-2 bg-black text-white hover:bg-black/90 h-8 sm:h-10 text-sm"
              >
                <UserPlus className="h-4 w-4" />
                {sendingInvite ? "Sending Invite..." : "Send Invite"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      <ErrorModal
        isOpen={showErrorModal}
        message={errorMessage}
        onClose={() => {
          setShowErrorModal(false);
          setErrorMessage("");
        }}
      />
    </div>
  );
}

export default IglooMembers;
